import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'

import { F0Box, F0Icon, F0Text } from '@factorialco/f0-react'
import { Dropdown, Switch, type DropdownItem } from '@factorialco/f0-react/dist/experimental'
import { Delete, Ellipsis, Handle, Pencil } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import type { MergedField } from './types'

type Props = {
  field: MergedField
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  onToggleVisibility: (fieldKey: string, newVisible: boolean) => void
  onToggleMandatory: (fieldKey: string, newRequired: boolean) => void
  onEdit: (field: MergedField) => void
  onDelete: (fieldKey: string) => void
}

function SectionField({
  field,
  dragHandleProps,
  onToggleVisibility,
  onToggleMandatory,
  onEdit,
  onDelete,
}: Props) {
  // Mandatory-locked fields cannot be made optional: backed by NOT NULL columns
  // on purchase_orders so they must always be filled in. Computed fields are
  // auto-filled by the system, so the toggle is allowed but has no practical effect.
  const canToggleMandatory = !field.locked

  const fieldActions: DropdownItem[] = []

  if (canToggleMandatory) {
    fieldActions.push({
      label: field.required
        ? i18n.t('procurement.purchase_requests.settings.po_templates.detail.mark_as_optional')
        : i18n.t('procurement.purchase_requests.settings.po_templates.detail.mark_as_mandatory'),
      onClick: () => onToggleMandatory(field.key, !field.required),
    })
  }

  if (!field.predefined) {
    fieldActions.push(
      {
        label: i18n.t('common.edit'),
        icon: Pencil,
        onClick: () => onEdit(field),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => onDelete(field.key),
      }
    )
  }

  return (
    <F0Box display="flex" alignItems="center" justifyContent="between" paddingX="md" paddingY="sm">
      <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
        {dragHandleProps && (
          <F0Box
            {...dragHandleProps}
            display="inline-flex"
            data-testid={`field-drag-handle-${field.key}`}
            aria-label={i18n.t(
              'procurement.purchase_requests.settings.po_templates.detail.reorder_field'
            )}
          >
            <F0Icon icon={Handle} size="xs" color="secondary" />
          </F0Box>
        )}
        <F0Text content={field.displayLabel} variant="body" required={field.required} />
      </F0Box>
      <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
        <Switch
          checked={field.visible}
          onCheckedChange={(checked) => onToggleVisibility(field.key, checked)}
          title={field.displayLabel}
          hideLabel
          disabled={field.locked}
        />
        <Dropdown
          items={fieldActions}
          icon={Ellipsis}
          size="sm"
          disabled={field.locked}
          data-testid={`field-actions-${field.key}`}
        />
      </F0Box>
    </F0Box>
  )
}

export default SectionField
